import { isChecklistCustomFieldSelected } from "./checklistConditionalRequired"

/**
 * Утилиты блокировки полей чеклиста по заполненности AMO-полей.
 *
 * Модуль не обращается к DOM и глобальному AMOCRM напрямую: он принимает уже
 * прочитанные значения AMO-полей и возвращает чистый результат для state,
 * сервисов сохранения и UI-подсказок.
 */

/**
 * Проверяет, считается ли текущее значение AMO-поля заполненным.
 *
 * Функция покрывает скалярные поля, чекбоксы и мультисписки. Пустые строки,
 * пробельные строки, null/undefined и пустые массивы считаются незаполненными;
 * число 0 считается реальным введенным значением.
 */
export const isAmoFieldFilled = (value) => {
    if (value === null || value === undefined) {
        return false
    }

    if (Array.isArray(value)) {
        return value.some(isAmoFieldFilled)
    }

    if (typeof value === "boolean") {
        return value === true
    }

    if (typeof value === "string") {
        return value.trim() !== ""
    }

    return true
}

const normalizeFieldId = (fieldId) => {
    if (fieldId === null || fieldId === undefined) {
        return null
    }

    return String(fieldId)
}

const getMissingPrerequisiteFieldIds = (rule, amoValuesByFieldId = {}) => {
    return (rule?.prerequisite_amo_field_ids || [])
        .map(normalizeFieldId)
        .filter(fieldId => fieldId !== null)
        .filter(fieldId => !isAmoFieldFilled(amoValuesByFieldId[fieldId]))
}

/**
 * Возвращает id полей чеклиста, заблокированных активными AMO-предусловиями.
 *
 * Правило активно, если хотя бы одно из его AMO-полей-предусловий сейчас пустое.
 * Результат нужен для UI-disable и не меняет расчет процента заполнения.
 */
export const getBlockedAmoPrerequisiteFieldIds = (
    checklist,
    amoValuesByFieldId = {}
) => {
    const blockedFieldIds = new Set()

    ;(checklist?.amo_prerequisite_rules || []).forEach(rule => {
        const missingFieldIds = getMissingPrerequisiteFieldIds(
            rule,
            amoValuesByFieldId
        )
        if (missingFieldIds.length === 0) {
            return
        }

        ;(rule.blocked_custom_field_ids || []).forEach(fieldId => {
            if (fieldId !== null && fieldId !== undefined) {
                blockedFieldIds.add(fieldId)
            }
        })
    })

    return blockedFieldIds
}

/**
 * Возвращает AMO-поля, из-за которых конкретное поле чеклиста заблокировано.
 *
 * Список используется для понятной подсказки пользователю: какие поля карточки
 * сделки нужно заполнить перед заполнением поля чеклиста.
 */
export const getMissingAmoPrerequisiteFieldsForCustomField = (
    checklist,
    customFieldId,
    amoValuesByFieldId = {}
) => {
    const missingFieldIds = new Set()

    ;(checklist?.amo_prerequisite_rules || []).forEach(rule => {
        const blocksCustomField = (rule.blocked_custom_field_ids || [])
            .includes(customFieldId)
        if (!blocksCustomField) {
            return
        }

        getMissingPrerequisiteFieldIds(rule, amoValuesByFieldId).forEach(fieldId => {
            missingFieldIds.add(fieldId)
        })
    })

    return Array.from(missingFieldIds)
}

/**
 * Определяет, нужно ли отключить поле чеклиста прямо в интерфейсе.
 *
 * Уже заполненное поле не отключается, потому что пользователь должен иметь
 * возможность очистить ошибочно проставленное значение. Попытку заменить его
 * на другое непустое значение дополнительно отсекает сервисный guard.
 */
export const shouldDisableAmoPrerequisiteField = (
    checklist,
    customFieldId,
    amoValuesByFieldId = {}
) => {
    const blockedFieldIds = getBlockedAmoPrerequisiteFieldIds(
        checklist,
        amoValuesByFieldId
    )
    if (!blockedFieldIds.has(customFieldId)) {
        return false
    }

    const customField = (checklist?.custom_fields || []).find(
        field => field.id === customFieldId
    )

    return !isChecklistCustomFieldSelected(customField)
}

/**
 * Проверяет, можно ли применить новое значение к полю чеклиста.
 *
 * Для заблокированного поля разрешается только переход в пустое состояние.
 * Это закрывает обход через уже активные чекбоксы, селекты и мультисписки.
 */
export const canApplyChecklistFieldValue = (
    checklist,
    customFieldId,
    nextContent,
    amoValuesByFieldId = {}
) => {
    const blockedFieldIds = getBlockedAmoPrerequisiteFieldIds(
        checklist,
        amoValuesByFieldId
    )
    if (!blockedFieldIds.has(customFieldId)) {
        return true
    }

    const customField = (checklist?.custom_fields || []).find(
        field => field.id === customFieldId
    )
    if (!customField) {
        return true
    }

    return !isChecklistCustomFieldSelected({
        ...customField,
        content: nextContent,
    })
}

/**
 * Собирает карту значений только для AMO-полей, участвующих в prerequisite rules.
 *
 * Чтение значений AMO потенциально дорогое и зависит от карточки сделки, поэтому
 * вызывающий код передает адаптер чтения, а модуль сохраняет чистый контракт.
 */
export const buildAmoPrerequisiteValuesMap = (
    checklists = [],
    getAmoFieldValue = () => null
) => {
    const amoFieldIds = new Set()

    checklists.forEach(checklist => {
        ;(checklist?.amo_prerequisite_rules || []).forEach(rule => {
            ;(rule?.prerequisite_amo_field_ids || []).forEach(fieldId => {
                const normalizedFieldId = normalizeFieldId(fieldId)
                if (normalizedFieldId !== null) {
                    amoFieldIds.add(normalizedFieldId)
                }
            })
        })
    })

    const amoValuesByFieldId = {}
    amoFieldIds.forEach(fieldId => {
        amoValuesByFieldId[fieldId] = getAmoFieldValue(fieldId)
    })

    return amoValuesByFieldId
}
